// Complete responsive version of HistoryComponent.tsx (with visual separation for records)

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../app/store';
import { getUserHistory, History } from '../features/userSlice';
import ReactPaginate from 'react-paginate';
import { formatCurrencyWithSymbol, getImagePath } from '../utils/utility';

interface LocalHistory {
  date: string;
  openedBoxDescription: string;
  boxTitle: String;
  boxAmount: number;
  itemWon: string;
  itemAmount: number;
  boxId: string;
  shipmentStatus: string;
  boxPicture: string;
  itemPicture: string;
  status: boolean;
}

const convertToLocalHistory = (apiHistory: History): LocalHistory => {
  return {
    date: new Date(apiHistory.timestamp + 'Z').toLocaleString(),
    openedBoxDescription: apiHistory.box_opened?.description ?? "box desc unknown",
    boxTitle: apiHistory.box_opened?.name ?? "box unknown",
    boxAmount: apiHistory.box_opened?.final_value ?? 0,
    itemWon: apiHistory.item_won.name,
    itemAmount: apiHistory.item_won.price,
    boxId: apiHistory.box_id,
    shipmentStatus: apiHistory.accepted ? apiHistory.shipped ? "Shipped" : "Processing" : "Exchanged",
    boxPicture: apiHistory.box_opened?.picture ?? "",
    itemPicture: apiHistory.item_won?.picture ?? "",
    status: apiHistory.accepted,
  };
};

const Container = styled.div`
  background: transparent;
  margin-top: 1rem;
  width: 100%;
  border: 1px solid #004d4d;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  background-color: #000d0d;
  color: #d9ffff;
  height: 40px;
  padding-left: 1rem;
  min-height: 40px;

  span {
    font-weight: bold;
  }
`;

const TransactionTable = styled.div`
  display: table;
  width: 100%;
  table-layout: fixed;
`;

interface RowProps {
  isHeading?: boolean;
}

const Row = styled.div<RowProps>`
  display: table-row;
  width: 100%;
  background-color: ${(props) => (props.isHeading ? '#004d4d' : 'transparent')};
  color: ${(props) => (props.isHeading ? '#ccfffb' : '#ffffff')};
  border-bottom: 1px solid #004d4d;

  @media (max-width: 1200px) {
    display: flex;
    flex-direction: column;
    background-color: #001111;
    border: 1px solid #005555;
    margin: 10px 0;
    padding: 12px;
    border-radius: 8px;
    box-shadow: 0 2px 6px rgba(0, 255, 255, 0.1);
  }
`;

const Cell = styled.div`
  display: table-cell;
  padding: 8px 16px;
  text-align: center;
  vertical-align: middle;

  @media (max-width: 1200px) {
    display: flex;
    justify-content: space-between;
    padding: 6px 12px;
    font-size: 14px;
    border-bottom: 1px solid #003d3d;

    &::before {
      content: attr(data-label);
      font-weight: bold;
      color: #00bcd4;
      margin-right: 10px;
    }

    &:last-child {
      border-bottom: none;
    }
  }
`;

const HeadingRow = styled.div`
  display: table-row;
  background-color: #004d4d;
  color: #ccfffb;
  font-weight: bold;
  text-align: center;
  height: 48px;

  @media (max-width: 1200px) {
    display: none;
  }
`;

const HeadingCell = styled.div`
  display: table-cell;
  padding: 8px 16px;
  text-align: center;
  border-bottom: 1px solid #004d4d;
`;

const FullSpanRow = styled.div`
  display: table-row;

  .full-cell {
    display: table-cell;
    padding: 16px;
    text-align: center;
    font-style: italic;
    color: #ffcccc;
    background: #001111;
    border-bottom: 1px solid #004d4d;
  }

  @media (max-width: 1200px) {
    display: block;

    .full-cell {
      display: block;
      width: 100%;
      padding: 12px;
    }
  }
`;





const DetailCell = styled(Cell) <{ status?: boolean }>`
  text-align: left;
  color: ${({ status }) => (status === false ? "orange" : "inherit")};
  word-break: break-word;
`;

const AmountCell = styled(Cell) <{ type: 'Credit' | 'Debit' }>`
  color: ${(props) => (props.type === 'Credit' ? '#28a745' : '#ff4c4c')};
`;

const DateCell = styled(Cell)`
  flex-basis: 20%;
`;

const HeadBalance = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #004d4d;
  height: 50px;
  padding-left: 1rem;
`;

const Title = styled.span`
  font-size: 1.5rem;
  font-weight: bold;
  color: white;
`;

const Spacer = styled.div`
  height: 100px;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0;
`;

export const BoxImage = styled.img`
  width: 50px;
  height: 50px;
  object-fit: contain;
  margin-right: 10px;
`;

const StyledLink = styled.a`
  text-decoration: none;
  color: #00bcd4;
  font-weight: bold;
  display: flex;
  align-items: center;

  &:hover {
    text-decoration: underline;
  }
`;

const getImageSource = (pic?: string): string =>
  pic && /\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(pic)
    ? getImagePath(pic)
    : "/assets/default-box.png";

const BoxDetailCell = ({ history }: { history: LocalHistory }) => {
  const baseUrl = window.location.origin;
  const boxLink = `${baseUrl}/boxes/${history.boxId}`;
  return (
    <DetailCell data-label="Opened Box">
      <StyledLink href={boxLink} target="_blank" rel="noopener noreferrer">
        <BoxImage src={getImageSource(history.boxPicture)} />
        <span>{history.boxTitle}</span>
      </StyledLink>
    </DetailCell>
  );
};

const ItemDetailCell = ({ history }: { history: LocalHistory }) => {
  return (
    <DetailCell data-label="Item Won">
      <BoxImage src={getImageSource(history.itemPicture)} />
      <span>{history.itemWon}</span>
    </DetailCell>
  );
};

const HistoryComponent: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(20);
  const dispatch: AppDispatch = useDispatch();
  const history = useSelector((state: RootState) => state.user.history || []);
  const isHistoryLoading = useSelector((state: RootState) => state.user.isHistoryLoading);
  const historyError = useSelector((state: RootState) => state.user.historyError);
  const totalHistory = useSelector((state: RootState) => state.user.totalHistory);

  const offset = (currentPage - 1) * limit;

  useEffect(() => {
    dispatch(getUserHistory({ offset, limit }));
  }, [dispatch, offset, limit]);

  const handlePageChange = (selectedPage: number) => {
    setCurrentPage(selectedPage);
  };

  const formattedHistory: LocalHistory[] = history.map(convertToLocalHistory);

  return (
    <>
      <Container>
        <HeadBalance>
          <Title>History</Title>
        </HeadBalance>
        <Head>
          <span>Your Recent Box Activities</span>
        </Head>
        <TransactionTable>
          <HeadingRow>
            <HeadingCell>Date</HeadingCell>
            <HeadingCell>Opened Box</HeadingCell>
            <HeadingCell>Opening Cost</HeadingCell>
            <HeadingCell>Item Won</HeadingCell>
            <HeadingCell>Amount</HeadingCell>
            <HeadingCell>Status</HeadingCell>
          </HeadingRow>
          {formattedHistory.map((history, index) => (
            <Row key={index}>
              <DateCell data-label="Date">{history.date}</DateCell>
              <BoxDetailCell history={history} />
              <AmountCell data-label="Opening Cost" type='Debit'>
                {formatCurrencyWithSymbol(history.boxAmount)}
              </AmountCell>
              <ItemDetailCell history={history} />
              <AmountCell data-label="Amount" type='Credit'>
                {formatCurrencyWithSymbol(history.itemAmount)}
              </AmountCell>
              <DetailCell data-label="Status" status={history.status}>{history.shipmentStatus}</DetailCell>
            </Row>
          ))}
        </TransactionTable>

        {isHistoryLoading || historyError && (
          <div style={{ padding: "40px", textAlign: "center", color: "#ffcccc", fontStyle: "italic", background: "#001111", borderBottom: "1px solid #004d4d" }}>
            {isHistoryLoading ? "Loading history..." : "Error loading history"}
          </div>
        )}
        <PaginationWrapper>
          <ReactPaginate
            pageCount={Math.ceil(totalHistory / limit)}
            pageRangeDisplayed={5}
            marginPagesDisplayed={2}
            onPageChange={({ selected }) => handlePageChange(selected + 1)}
            previousLabel={'Previous'}
            nextLabel={'Next'}
            breakLabel={'...'}
            containerClassName={'pagination'}
            pageClassName={'page-item'}
            pageLinkClassName={'page-link'}
            previousClassName={'page-item'}
            previousLinkClassName={'page-link'}
            nextClassName={'page-item'}
            nextLinkClassName={'page-link'}
            breakClassName={'page-item'}
            breakLinkClassName={'page-link'}
            activeClassName={'active'}
            forcePage={currentPage - 1}
          />
        </PaginationWrapper>
      </Container>
    </>
  );
};

export default HistoryComponent;
